import { createAppSelector, orgUserSelectors } from "./base";

import type { RootState } from "store";
import { filterMap } from "utils/collections";
import { getEnabledOrgRole } from "utils/roles";

export const selectOrgIdForSectionId = (state: RootState, sectionId: string | null | undefined) =>
  sectionId && state.auth.section_to_org_id_map && sectionId in state.auth.section_to_org_id_map
    ? state.auth.section_to_org_id_map[sectionId]
    : null;

export const selectOrgNameBySectionId = createAppSelector(
  [
    (state, sectionId: string) => state.auth.section_to_org_id_map?.[sectionId],
    (state) => state.orgs,
  ],
  (orgId, orgs) => (orgId ? orgs[orgId]?.name || "" : ""),
);

export const selectCurrentOrg = createAppSelector(
  [(state: RootState) => state.orgs],
  (orgs) => Object.values(orgs).find((o) => o.current) || null,
);

export const selectLmsType = createAppSelector([selectCurrentOrg], (org) => org?.lms_type || null);

export const selectCurrentOrgName = createAppSelector(
  [selectCurrentOrg],
  (org) => org?.name || null,
);

export const selectCurrentOrgRubric = createAppSelector(
  [selectCurrentOrg],
  (org) => org?.rubric || null,
);

export const selectDisabledUserIds = createAppSelector(
  [orgUserSelectors.selectAll],
  (orgUsers) =>
    new Set(
      filterMap(
        orgUsers,
        (ou) => ou.is_disabled,
        (ou) => ou.user_id,
      ),
    ),
);

export const selectOrgUserByUserIdMap = createAppSelector(
  [orgUserSelectors.selectAll, selectCurrentOrg],
  (orgUsers, currentOrg) =>
    Object.fromEntries(
      filterMap(
        orgUsers,
        (ou) => ou.org_id === currentOrg?.id,
        (ou) => [ou.user_id, ou],
      ),
    ),
);

export const selectOrgUserByUserId = createAppSelector(
  [selectOrgUserByUserIdMap, (state, userId: string) => userId],
  (orgUserMap, userId) => orgUserMap[userId] ?? null,
);

export const selectCurrentOrgUser = createAppSelector(
  [orgUserSelectors.filterForCurrentUser, selectCurrentOrg],
  (orgUsers, currentOrg) => orgUsers.find((ou) => ou.org_id === currentOrg?.id) ?? null,
);

export const selectCurrentOrgRole = createAppSelector([selectCurrentOrgUser], getEnabledOrgRole);

export const selectOrgRoleByUserId = createAppSelector([selectOrgUserByUserId], getEnabledOrgRole);

export const selectIsOrgUserDisabled = createAppSelector(
  [selectOrgUserByUserId],
  (orgUser) => orgUser?.is_disabled ?? false,
);

export const selectIsCurrentOrgUserDisabled = createAppSelector(
  [selectCurrentOrgUser],
  (orgUser) => orgUser?.is_disabled ?? false,
);

export const selectMaxAvActiveParticipants = createAppSelector(
  [selectCurrentOrg],
  (org) => org?.max_av_active_participants || 15,
);
